import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Bell, Lock } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Button } from '@/app/components/ui/button';
import { Switch } from '@/app/components/ui/switch';
import { Label } from '@/app/components/ui/label';
import { Skeleton } from '@/app/components/ui/skeleton';
import { Alert, AlertDescription } from '@/app/components/ui/alert';
import { toast } from '@/app/components/ui/simple-toaster';
import { queryClient, queryKeys } from '@/lib/query-client';
import {
  useNotificationPreferences,
  useUpdateNotificationPreference,
} from '@/hooks/use-notification-preferences';

const MODULE_GROUPS = [
  { module: 'H3Gear', categories: ['equipment_inspections', 'gear_requests'] },
  { module: 'H3Crew', categories: ['certifications', 'assignments', 'travel', 'leave_requests'] },
  { module: 'H3Safe', categories: ['jsa_signatures', 'safety_reports'] },
  { module: 'H3Books', categories: ['project_updates', 'daily_reports', 'dispatch'] },
  { module: 'H3Pay', categories: ['timesheets', 'expenses'] },
  { module: 'H3Comply', categories: ['compliance_proposals'] },
  { module: 'Platform', categories: ['invitations', 'general'] },
  { module: 'Billing', categories: ['invoices'] },
] as const;

// Safety-critical categories cannot be silenced server-side and are locked in the UI.
const SAFETY_CRITICAL_CATEGORIES = new Set([
  'equipment_inspections',
  'safety_reports',
  'compliance_proposals',
]);

export function NotificationPreferencesCard() {
  const { data: preferences, isLoading, error } = useNotificationPreferences();
  const updatePreference = useUpdateNotificationPreference();
  const [enabledByCategory, setEnabledByCategory] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (preferences) {
      const map: Record<string, boolean> = {};
      preferences.forEach((p) => {
        // Safety-critical categories are always treated as enabled.
        map[p.category] = SAFETY_CRITICAL_CATEGORIES.has(p.category) ? true : p.enabled;
      });
      setEnabledByCategory(map);
    }
  }, [preferences]);

  const hasChanges = useMemo(() => {
    if (!preferences) return false;
    return preferences.some((p) => {
      if (SAFETY_CRITICAL_CATEGORIES.has(p.category)) return false;
      return enabledByCategory[p.category] !== p.enabled;
    });
  }, [preferences, enabledByCategory]);

  const handleToggle = (category: string) => {
    if (SAFETY_CRITICAL_CATEGORIES.has(category)) return;
    setEnabledByCategory((prev) => ({ ...prev, [category]: !prev[category] }));
  };

  const handleReset = () => {
    if (preferences) {
      const map: Record<string, boolean> = {};
      preferences.forEach((p) => {
        map[p.category] = SAFETY_CRITICAL_CATEGORIES.has(p.category) ? true : p.enabled;
      });
      setEnabledByCategory(map);
    }
  };

  const handleSave = async () => {
    if (!preferences) return;

    const changed = preferences.filter((p) => {
      if (SAFETY_CRITICAL_CATEGORIES.has(p.category)) return false;
      return enabledByCategory[p.category] !== p.enabled;
    });
    if (changed.length === 0) return;

    const results = await Promise.allSettled(
      changed.map((p) =>
        updatePreference.mutateAsync({
          category: p.category,
          enabled: enabledByCategory[p.category]!,
        })
      )
    );

    const failed: typeof changed = [];
    results.forEach((result, i) => {
      if (result.status === 'rejected') {
        failed.push(changed[i]!);
      }
    });

    // Reconcile local state with the server after any partial failure so the
    // UI never shows a stale success state.
    await queryClient.refetchQueries({ queryKey: queryKeys.notificationPreferences.all });

    if (failed.length === 0) {
      toast.success('Notification Preferences Saved', {
        description: 'Your notification category preferences have been updated.',
      });
    } else {
      const names = failed.map((f) => f.label).join(', ');
      toast.error('Some preferences failed to save', {
        description: `Could not save: ${names}. Please try again.`,
      });
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-3/4" />
        </CardHeader>
        <CardContent className="space-y-6">
          <Skeleton className="h-8" />
          <Skeleton className="h-8" />
          <Skeleton className="h-8" />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="border-red-200 bg-red-50 dark:border-red-900 dark:bg-red-950">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="font-semibold text-red-900 dark:text-red-100">
                Error Loading Preferences
              </h3>
              <p className="text-sm text-red-700 dark:text-red-200 mt-1">
                Failed to load your notification preferences. Please try refreshing the page.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!preferences) return null;

  const preferenceMap = new Map(preferences.map((p) => [p.category, p]));

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start sm:items-center justify-between gap-4 flex-col sm:flex-row">
          <div className="space-y-1">
            <CardTitle className="flex items-center gap-2">
              <Bell className="h-5 w-5" />
              Notification Preferences
            </CardTitle>
            <CardDescription>
              Choose which notification categories you receive. Safety-critical categories
              (equipment inspections, safety reports, compliance proposals) cannot be disabled.
            </CardDescription>
          </div>
          <div className="flex gap-2 shrink-0">
            {hasChanges && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleReset}
                disabled={updatePreference.isPending}
              >
                Reset
              </Button>
            )}
            <Button
              size="sm"
              onClick={handleSave}
              disabled={!hasChanges || updatePreference.isPending}
            >
              {updatePreference.isPending ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        <Alert className="bg-amber-50 border-amber-200 dark:bg-amber-950 dark:border-amber-900">
          <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
          <AlertDescription className="text-amber-800 dark:text-amber-200 text-sm">
            Failed inspections, near-miss reports, and compliance proposals are safety-critical
            and cannot be disabled. Enabling other categories is optional.
          </AlertDescription>
        </Alert>

        {MODULE_GROUPS.map((group) => (
          <div key={group.module}>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-foreground mb-3">
              {group.module}
            </h3>
            <div className="space-y-4">
              {group.categories.map((category) => {
                const pref = preferenceMap.get(category);
                if (!pref) return null;
                const isLocked = SAFETY_CRITICAL_CATEGORIES.has(category);
                const enabled = isLocked ? true : (enabledByCategory[category] ?? pref.enabled);

                return (
                  <div
                    key={category}
                    className="flex items-start justify-between gap-4 pb-4 border-b last:border-0 last:pb-0"
                  >
                    <div className="space-y-1">
                      <Label
                        htmlFor={`notification-pref-${category}`}
                        className={`font-medium text-sm flex items-center gap-2 ${
                          isLocked ? 'text-gray-500 dark:text-gray-400' : ''
                        }`}
                      >
                        {pref.label}
                        {isLocked && (
                          <Lock className="h-3 w-3 text-gray-500" aria-hidden="true" />
                        )}
                      </Label>
                      {isLocked ? (
                        <p className="text-xs text-amber-600 dark:text-amber-400">
                          This category is required and cannot be disabled.
                        </p>
                      ) : (
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          Toggle to enable or disable notifications.
                        </p>
                      )}
                    </div>
                    <Switch
                      id={`notification-pref-${category}`}
                      checked={enabled}
                      onCheckedChange={() => handleToggle(category)}
                      disabled={isLocked || updatePreference.isPending}
                      aria-label={`Toggle ${pref.label} notifications`}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
